import { Process } from "../../processEditor/model/Process";
import { HashMap } from "../../shared/model/HashMap";
import { Option } from "../../shared/model/Option";

export interface IAppReducerState {
    state: IAppReducerOwnState;
    dispatch: (payload: IAppReducerAction) => void;
}

export interface IAppReducerOwnState {
    environments: IEnvironmentData[];
    rules: IRuleData[] | null;
    selectedRuleId: number | null;
    isLoading: boolean;
    isDirty: boolean;
    saveButtonClicked: boolean;
    showNewRuleEditor: boolean;
    promotedVariables: Option[];
    searchString: string;
    userPermissions: HashMap<boolean>;
    timeZones: RuleTimeZone[] | null;
}

export type IAppReducerAction =
    | { type: "SET_ENVIRONMENTS", environments: IEnvironmentData[] }
    | { type: "SET_RULES", rules: IRuleData[] }
    | { type: "SET_SELECTED_RULE_ID", id: number | null }
    | { type: "SET_IS_LOADING", isLoading: boolean }
    | { type: "SET_IS_DIRTY", isDirty: boolean }
    | { type: "SET_SAVE_BUTTON_CLICKED", saveButtonClicked: boolean }
    | { type: "SET_SHOW_NEW_RULE_EDITOR", showNewRuleEditor: boolean }
    | { type: "SET_PROMOTED_VARIABLES", promotedVariables: Option[] }
    | { type: "SET_SEARCH_STRING", searchString: string }
    | { type: "SET_USER_PERMISSIONS", userPermissions: HashMap<boolean> }
    | { type: "SET_TIME_ZONES", timeZones: RuleTimeZone[] }
    | { type: "GET_ENVIRONMENTS" }
    | { type: "GET_CURRENT_ENVIRONMENT" }
    | { type: "GET_RULES" }
    | { type: "GET_IS_LOADING" }
    | { type: "GET_SHOW_NEW_RULE_EDITOR" }
    | { type: "GET_SHOW_NEW_RULE_EDITOR_BUTTON" }
    | { type: "GET_PROMOTED_VARIABLES" }
    | { type: "GET_SEARCH_STRING" }
    | { type: "GET_USER_PERMISSIONS" }
    | { type: "GET_TIME_ZONES" };

export enum ERuleStatus {
    Ok,
    Alert,
    Unknown
}

export interface IRuleData {
    id: number;
    environmentName: string;
    config: IRuleDataConfig;
    series: IRuleDataSeries[];
    alertsCount: number;
    name: string;
    lastStatus?: ERuleStatus;
    description?: string;
    seriesDataAvailable?: boolean;
    chartData: IRuleDataPoint[];
    emailRecipients: string[];
    isDeactivated?: boolean;
}
export interface RuleTimeZone {
    id: string;
    description: string;
    baseOffset: number;
}

export interface IChartTooltipData {
    row: {
        x: Date,
        y0: number,
        y1: number
    };
    series: {
        axis: string,
        color: string,
        dataset: string,
        id: string,
        label: string,
        visible: string
    };
}

export interface IRuleDataConfig {
    isActiveEveryDay: boolean;
    activeWeekDays: string[];
    isActiveEntireDay: boolean;
    expressionOperator: string;
    expressionExpectedValue: string;
    timeZone: string;
    timePeriodType: string;
    timePeriod: number;
    aggregate: string;
    operator: string;
    name: string;
    variableName: string;
    showAdvancedTimeIntervalSettings?: boolean;
    startHour: number;
    endHour: number;
    expectedValue: number;
    environmentName?: string;
    processTriggerValues?: IProcessTriggerValue[];
    disablingHolidaysAreEnabled?: boolean;
    disablingHolidays: IPublicHoliday[];
}

export interface IProcessTriggerValueOption extends Option {
    agentGroupName: string;
}

export interface IProcessTriggerValue {
    agentGroupName: string;
    processGuid: string;
    agentGroupDisplayName?: string | null;
    processName?: string | null;
}

export interface IProcessesByName {
    agentGroupName: string;
    agentGroupDisplayName: string;
    processListDatas: Process[];
}

export interface AgentGroupWithProcesses {
    agentGroupName: string;
    agentGroupDisplayName: string;
    processes: Process[];
}

export interface IRuleDataSeries {
    timestamp: string;
    value: number | null;
    alert: IDataSeriesAlert;
}

interface IDataSeriesAlert {
    errorString: string;
    expectedValue: number;
    acknowledged: boolean;
    failedWindowStartTime: string;
    ruleId: number;
}

export interface IRuleDataPoint {
    x: number;
    series: number | null;
    alert: number | null;
    seriesLabel: string;
    alertLabel: string;
}

export interface IEnvironmentData {
    name: string;
    displayName: string;
}

export interface IAgentGroupData {
    id: number;
    name: string;
    displayName: string;
    executionAgents: IAgentData[];
    gatewayAgents: IAgentData[];
}

export interface IAgentData {
    id: number;
    name: string;
    isGateway: boolean;
}

export interface IPublicHolidaysResult {
    holidays: IPublicHoliday[];
}

export interface IPublicHoliday {
    date: string;
    localName: string;
    name: string;
    countryCode: string;
    fixed: boolean;
    global: boolean;
    counties?: null;
    launchYear?: null;
    types?: (string)[] | null;
  }
  